import React from 'react';

import Avatar from '@/core/components/Avatar';
import Button from '@/core/components/Button';
import ContributionCard from '@/core/components/Contribution/ContributionCard';
import Icons from '@/core/components/Icons';
import MenuCard from '@/core/components/MenuCard';
import MenuItem from '@/core/components/MenuItem';
import NotifBadge from '@/core/components/NotifBadge';
import useOpenGive from '@/core/hooks/useOpenGive';
import { AnalyticsEventType, useAnalyticsContext } from '@/core/lib/analytics/analytics.context';
import { useAuthContext } from '@/core/lib/auth/auth.context';
import { useStripeFlag } from '@/core/lib/ff/ff.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import Store from '@/core/lib/new-architecture/store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface ProfileMenuProps {}

const ProfileMenu: React.FC<ProfileMenuProps> = () => {
  const { Link: RouterLink, push } = useRouterContext();
  const { t } = useTranslationContext(['profile.profile', 'contribute']);
  const { logout, openAuth } = useAuthContext();
  const { send } = useAnalyticsContext();
  const { data: current } = Store.current.useCurrent();
  const { isNative } = useNativeContext();
  const { onOpenGive } = useOpenGive();
  const { Link } = useRouterContext();

  const isStripeActivated = useStripeFlag();

  const onLogout = async () => {
    await send({ event: AnalyticsEventType.LOGOUT });
    await logout();
  };

  const { data: conversations } = Store.conversations.useConversations();
  const conversationsUnread = conversations?.getUnread() ?? 0;

  const { data: favorites } = Store.favorites.useFavorites();
  const favoritesUnread = favorites?.getUnread() ?? 0;

  const onClickOpenGive = async () => {
    await send({ event: AnalyticsEventType.CLICK_GIVE, where: 'Menu Profil' });
    onOpenGive();
  };

  const contributionButtonTranslationKey = current?.displayAds() ? 'contribution-menu-card.button-text' : 'contribution-menu-card.button-text-already-contribute';

  if (!current) {
    if (isNative) return null;
    return (
      <div className="lg:hidden">
        {/* drawer header */}
        <div className="relative flex justify-start bg-bg-primary py-3">
          <p className="text-body-primary w-full text-center font-semibold text-content-secondary">{t('menu', { ns: 'profile.profile' })}</p>
          <div className="absolute right-4 top-3 flex size-6 cursor-pointer">
            <Icons icon="close" size="24" />
          </div>
        </div>

        {/* disconnected user - menu block */}
        <div className="mb-4 flex flex-col gap-4 overflow-y-auto bg-bg-primary px-4">
          {/* Donation button */}
          <Button
            onClick={async () => {
              await send({ event: AnalyticsEventType.CLICK_AUTH, where: 'Menu Profil + Give' });
              openAuth();
            }}
            variant="give"
            size="medium"
            iconLeft="add"
          >
            {t('components.header.give', { ns: 'common' })}
          </Button>

          <div className="mt-2 flex flex-col gap-2">
            {/* external links */}
            <div className="flex flex-col">
              <RouterLink href={new Routes.ExternalRoute('vision')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
                {t('components.header.vision', { ns: 'common' })}
              </RouterLink>

              <RouterLink href={new Routes.ExternalRoute('faq')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
                {t('components.header.faq', { ns: 'common' })}
              </RouterLink>

              <RouterLink href={new Routes.ExternalRoute('blog')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
                {t('components.header.blog', { ns: 'common' })}
              </RouterLink>
            </div>
            {/* Login button */}
            <div className="h-[1px] bg-stroke-secondary" />
            <button type="button" className="flex items-center justify-between space-x-1 bg-bg-primary px-3 py-4" onClick={openAuth}>
              <div className="flex items-center space-x-2">
                <Icons icon="log-in" size="24" />
                <div className="flex flex-col items-start">
                  <p className="text-body-secondary font-medium text-content-primary">{t('menu-items.login', { ns: 'profile.profile' })}</p>
                </div>
              </div>
            </button>

            <div className="h-[1px] bg-stroke-secondary" />
            {/* Download app buttons */}
            <div className="flex justify-center gap-3 px-3 py-4">
              <Link href={new Routes.ExternalRoute('appStore')} target="_blank" className="w-35">
                <img src="/assets/socialNetwork/app-store.png" alt={t('social-network.app-store-alt', { ns: 'common' })} />
              </Link>
              <Link href={new Routes.ExternalRoute('playStore')} target="_blank" className="w-35">
                <img src="/assets/socialNetwork/google-play.png" alt={t('social-network.play-store-alt', { ns: 'common' })} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const co2Icon = (
    <div className="flex size-12 items-center justify-center rounded-2 bg-bg-button-primary-default/20">
      <Icons weight="bold" icon="seedling" size="32" color="primary" />
    </div>
  );

  const { avatarUrl, username, co2 } = current.getInfo();
  const goToContributionPage = () => push(new Routes.ContributionRoute());

  return (
    <>
      {/* drawer header */}
      {!isNative && (
        <div className="relative flex justify-start bg-bg-primary py-3">
          <p className="text-body-primary w-full text-center font-semibold text-content-secondary">{t('menu', { ns: 'profile.profile' })}</p>
          <div className="absolute right-4 top-3 flex size-6 cursor-pointer lg:hidden">
            <Icons icon="close" size="24" />
          </div>
        </div>
      )}

      <div className={`mb-4 flex flex-col gap-4 overflow-y-auto bg-bg-primary ${isNative ? 'pb-14' : 'px-4'}`}>
        {/* public profile button */}
        <div className="flex">
          <MenuCard href={new Routes.PublicProfileRoute(current.uuid)} leftIcon={<Avatar avatarUrl={avatarUrl} size="48" />}>
            <p className="text-body-primary truncate font-bold text-content-primary">{username ?? t('profile-recap.default-label', { ns: 'profile.profile' })}</p>
            <p className="text-caption-primary font-medium text-content-placeholder">{t('profile-recap.detail', { ns: 'profile.profile' })}</p>
          </MenuCard>
          {/* for now disabled */}
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && (
            <MenuCard leftIcon={co2Icon}>
              <div className="my-0.5">
                <p className="text-caption-primary font-medium text-content-placeholder">{t('counter-recap.label', { ns: 'profile.profile' })}</p>
                <p className="text-title font-bold text-content-primary">{`${co2} ${t('counter-recap.emmissions', { ns: 'profile.profile' })}`}</p>
              </div>
            </MenuCard>
          )}
        </div>

        {/* Donation button */}
        {!isNative && (
          <div className="lg:hidden">
            <Button onClick={onClickOpenGive} variant="give" size="medium" iconLeft="add">
              {t('components.header.give', { ns: 'common' })}
            </Button>
          </div>
        )}

        {/* connected user - menu block */}
        <div className="flex flex-col gap-2">
          {isStripeActivated ? (
            <ContributionCard>
              <Button variant="brand" onClick={goToContributionPage}>
                {t(contributionButtonTranslationKey, { ns: 'contribute' })}
              </Button>
            </ContributionCard>
          ) : null}
          {/* connected user menu items */}
          <div>
            <div className="relative flex flex-col">
              <div className="absolute left-8 top-1">
                <NotifBadge unread={conversationsUnread} size="small" />
              </div>
              <MenuItem href={new Routes.ConversationsRoute()} content={t('menu-items.messages', { ns: 'profile.profile' })} leftIcon={<Icons icon="message-square" size="24" />} />
            </div>
            <div className="relative">
              <div className="absolute left-8 top-1">
                <NotifBadge unread={favoritesUnread} size="small" />
              </div>
              <MenuItem href={new Routes.FavoritesRoute()} content={t('menu-items.favorites', { ns: 'profile.profile' })} leftIcon={<Icons icon="heart" size="24" />} />
            </div>

            <MenuItem href={new Routes.ProfilePostsRoute()} content={t('menu-items.posts', { ns: 'profile.profile' })} leftIcon={<Icons icon="gift" size="24" />} />

            <MenuItem href={new Routes.ProfileSettingsRoute()} content={t('menu-items.settings', { ns: 'profile.profile' })} leftIcon={<Icons icon="settings" size="24" />} />

            <MenuItem href={new Routes.CguRoute()} isInAppBrowser target="_blank" content={t('menu-items.law', { ns: 'profile.profile' })} leftIcon={<Icons icon="law" size="24" />} />
          </div>

          {/* external links */}
          <div className="h-[1px] bg-stroke-secondary" />
          <div className="flex flex-col">
            <RouterLink href={new Routes.ExternalRoute('vision')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
              {t('components.header.vision', { ns: 'common' })}
            </RouterLink>

            <RouterLink href={new Routes.ExternalRoute('faq')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
              {t('components.header.faq', { ns: 'common' })}
            </RouterLink>

            <RouterLink href={new Routes.ExternalRoute('blog')} className="text-body-secondary shrink-0 px-3 py-4 font-normal text-content-secondary">
              {t('components.header.blog', { ns: 'common' })}
            </RouterLink>
          </div>

          {/* Logout button */}
          <div className="h-[1px] bg-stroke-secondary" />
          <button type="button" className="flex items-center justify-between space-x-1 bg-bg-primary px-3 py-4" onClick={onLogout}>
            <div className="flex items-center space-x-2">
              <Icons icon="log-out" size="24" />
              <p className="text-body-secondary font-medium text-content-primary">{t('menu-items.log-out', { ns: 'profile.profile' })}</p>
            </div>
          </button>

          {/* Download app buttons */}
          {!isNative && (
            <div className="flex flex-col gap-2 bg-bg-primary">
              <div className="h-[1px] bg-stroke-secondary" />
              <div className="flex justify-center gap-3 px-3 py-4 lg:hidden">
                <Link href={new Routes.ExternalRoute('appStore')} target="_blank" className="w-35">
                  <img src="/assets/socialNetwork/app-store.png" alt={t('social-network.app-store-alt', { ns: 'common' })} />
                </Link>
                <Link href={new Routes.ExternalRoute('playStore')} target="_blank" className="w-35">
                  <img src="/assets/socialNetwork/google-play.png" alt={t('social-network.play-store-alt', { ns: 'common' })} />
                </Link>
              </div>
              <div className="hidden items-center justify-between bg-bg-button-primary-default p-4 lg:flex">
                <div className="flex flex-col gap-3">
                  <p className="text-body-primary font-semibold text-content-button-primary">{t('app-texts.title', { ns: 'profile.profile' })}</p>
                  <p className="text-caption-primary text-content-button-primary">{t('app-texts.subtitle', { ns: 'profile.profile' })}</p>
                </div>
                <img src="/assets/appQRcode.png" alt={t('app-texts.title', { ns: 'profile.profile' })} className="size-28" />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileMenu;
